<script>
  import { accountAuthSection } from './account-auth.js';
  import {
    step,
    loginEmail,
    loginCode,
    isSubmitting,
    isResending,
    formError,
    codeError,
    setLoginEmail,
    setLoginCode,
    requestLoginCode,
    resendLoginCode,
    verifyLoginCode,
    backToLogin
  } from '../stores/account.js';

  function onEmailSubmit(event) {
    event.preventDefault();
    requestLoginCode();
  }

  function onCodeSubmit(event) {
    event.preventDefault();
    verifyLoginCode();
  }
</script>

<section class="account-auth" aria-labelledby="account-auth-title">
  <div class="account-auth-frame">
    {#if $step === 'verify'}
      <header class="account-auth-head">
        <h1 id="account-auth-title">{accountAuthSection.verify.title}</h1>
        <p>{accountAuthSection.verify.description}</p>
      </header>

      <form class="account-auth-form" on:submit={onCodeSubmit} novalidate>
        <label class="field-group">
          <span class="label">{accountAuthSection.verify.codeLabel}</span>
          <input
            class="input"
            class:has-error={$codeError && !/отправлен/i.test($codeError)}
            type="text"
            inputmode="numeric"
            maxlength="6"
            placeholder={accountAuthSection.verify.codePlaceholder}
            value={$loginCode}
            on:input={(e) => setLoginCode(e.currentTarget.value)}
          />
        </label>

        {#if $codeError}
          <p class="message" class:ok={/отправлен/i.test($codeError)} role="alert">{$codeError}</p>
        {/if}

        <button class="submit" type="submit" disabled={$isSubmitting || $isResending}>
          <span>{$isSubmitting ? accountAuthSection.verify.submitting : accountAuthSection.verify.submit}</span>
        </button>

        <div class="account-auth-aux">
          <button type="button" class="link" on:click={resendLoginCode} disabled={$isSubmitting || $isResending}>
            <span>{$isResending ? accountAuthSection.verify.resending : accountAuthSection.verify.resend}</span>
          </button>
          <button type="button" class="link muted" on:click={backToLogin} disabled={$isSubmitting || $isResending}>
            <span>{accountAuthSection.verify.back}</span>
          </button>
        </div>
      </form>
    {:else}
      <header class="account-auth-head">
        <h1 id="account-auth-title">{accountAuthSection.login.title}</h1>
        <p>{accountAuthSection.login.description}</p>
      </header>

      <form class="account-auth-form" on:submit={onEmailSubmit} novalidate>
        <label class="field-group">
          <span class="label">{accountAuthSection.login.emailLabel}</span>
          <input
            class="input"
            class:has-error={$formError}
            type="email"
            autocomplete="email"
            placeholder={accountAuthSection.login.emailPlaceholder}
            value={$loginEmail}
            on:input={(e) => setLoginEmail(e.currentTarget.value)}
          />
        </label>

        {#if $formError}
          <p class="message" role="alert">{$formError}</p>
        {/if}

        <button class="submit" type="submit" disabled={$isSubmitting}>
          <span>{$isSubmitting ? accountAuthSection.login.submitting : accountAuthSection.login.submit}</span>
        </button>
      </form>
    {/if}
  </div>
</section>

<style>
  .account-auth {
    display: flex;
    width: 100%;
    flex: 1 0 auto;
    align-items: flex-start;
    justify-content: center;
    padding: 100px 24px 80px;
    background: #000000;
    color: #ffffff;
    box-sizing: border-box;
  }

  .account-auth-frame {
    width: 100%;
    max-width: 480px;
    display: flex;
    flex-direction: column;
    gap: 32px;
  }

  .account-auth-head {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  h1 {
    margin: 0;
    font-size: 40px;
    font-weight: 700;
    line-height: 1.1;
  }

  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.7);
    font-size: 16px;
    line-height: 24px;
  }

  .account-auth-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .field-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .label {
    color: rgba(255, 255, 255, 0.7);
    font-size: 14px;
    font-weight: 500;
  }

  .input {
    width: 100%;
    height: 51px;
    border: 1px solid rgba(255, 255, 255, 0.5);
    border-radius: 16px;
    background: transparent;
    padding: 14px 16px;
    color: #ffffff;
    font-family: Roboto, Arial, sans-serif;
    font-size: 14px;
    outline: none;
    box-sizing: border-box;
    transition: border-color 160ms ease;
  }

  .input::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  .input:focus {
    border-color: #ffa800;
  }

  .input.has-error {
    border-color: #ff6b6b;
  }

  .message {
    margin: 0;
    color: #ff6b6b;
    font-size: 13px;
  }

  .message.ok {
    color: #e7b962;
  }

  .submit {
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    padding: 13px 23px;
    border: 0;
    border-radius: 5px;
    background: #e7b962;
    color: #000000;
    font-family: Roboto, Arial, sans-serif;
    font-size: 18px;
    font-weight: 500;
    line-height: 18px;
    text-transform: uppercase;
    cursor: pointer;
    transition: background-color 160ms ease;
  }

  .submit:hover:not([disabled]),
  .submit:focus-visible:not([disabled]) {
    background: #ffa800;
  }

  .submit[disabled] {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .account-auth-aux {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
  }

  .link {
    align-self: flex-start;
    background: none;
    border: 0;
    padding: 0;
    color: rgba(231, 185, 98, 0.85);
    font-family: Manrope, Roboto, Arial, sans-serif;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: color 160ms ease;
  }

  .link:hover:not([disabled]),
  .link:focus-visible:not([disabled]) {
    color: #e7b962;
  }

  .link.muted {
    color: rgba(255, 255, 255, 0.6);
  }

  .link.muted:hover:not([disabled]),
  .link.muted:focus-visible:not([disabled]) {
    color: #ffffff;
  }

  .link[disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 767px) {
    .account-auth {
      padding: 60px 24px;
    }

    h1 {
      font-size: 32px;
    }

    .submit {
      width: 100%;
    }
  }
</style>
